import { SCENARIO_COLOR_SLOTS, type ScenarioId } from '@finsim/model';
import { useUnit } from 'effector-react';
import { Copy, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $project, commandIssued } from '../../entities/project';
import {
  Button,
  ColorSwatches,
  EditOnly,
  Field,
  Section,
  Switch,
  TextField,
} from '../../shared/ui';
import { deleteVariantRequested, duplicateVariantRequested, setBaselineRequested } from './model';

export function ScenarioInspector({ scenarioId }: { scenarioId: ScenarioId }) {
  const { t } = useTranslation();
  const [project, issue, duplicate, remove, setBaseline] = useUnit([
    $project,
    commandIssued,
    duplicateVariantRequested,
    deleteVariantRequested,
    setBaselineRequested,
  ]);
  const scenario = project?.scenarios.find((s) => s.id === scenarioId);
  if (!project || !scenario) return null;
  const isBaseline = project.baselineScenarioId === scenarioId;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <header className="px-4 pt-3 pb-2">
          <p className="text-xs text-text-3">{t('scenario.title')}</p>
          <h2 className="truncate text-base font-medium">{scenario.name}</h2>
          <p className="mt-0.5 text-xs text-text-3">
            {t('scenario.items', { count: scenario.itemIds.length })}
          </p>
        </header>
        <Section title={t('inspector.sections.general')}>
          <Field label={t('scenario.name')}>
            {({ id }) => (
              <TextField
                id={id}
                value={scenario.name}
                maxLength={80}
                onCommit={(name) => {
                  issue({
                    command: { type: 'scenario/update', id: scenarioId, patch: { name } },
                    coalesceKey: `${scenarioId}:name`,
                  });
                }}
              />
            )}
          </Field>
          <Field label={t('scenario.color')}>
            {({ id }) => (
              <ColorSwatches
                id={id}
                label={t('scenario.color')}
                value={scenario.color}
                count={SCENARIO_COLOR_SLOTS}
                swatchLabel={(slot) => t('scenario.colorN', { n: slot + 1 })}
                onChange={(color) => {
                  issue({ command: { type: 'scenario/update', id: scenarioId, patch: { color } } });
                }}
              />
            )}
          </Field>
          <Field label={t('scenario.baseline')} hint={t('scenario.baselineHint')}>
            {({ id, describedBy }) => (
              <div className="pt-1">
                <Switch
                  id={id}
                  aria-describedby={describedBy}
                  checked={isBaseline}
                  disabled={isBaseline}
                  onCheckedChange={(on) => {
                    if (on) setBaseline({ scenarioId });
                  }}
                />
              </div>
            )}
          </Field>
        </Section>
      </div>
      <EditOnly>
        <footer className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 border-t border-border-subtle px-3 py-2">
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Copy />}
            onClick={() => {
              duplicate({
                scenarioId,
                mode: 'share',
                name: t('variants.copyName', { name: scenario.name }),
              });
            }}
          >
            {t('variants.duplicateShared')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Trash2 />}
            className="hover:text-negative"
            onClick={() => {
              remove({ scenarioId });
            }}
          >
            {t('variants.delete')}
          </Button>
        </footer>
      </EditOnly>
    </div>
  );
}
